import * as React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { CommonAppLayout } from './components/CommonAppLayout';
import { BREADCRUMBS, Pages } from './pages/pages';
import { FeatureFlags, useFeatureFlag } from './context/FeatureFlags';
import { DataPlaneDashboardPage } from './pages/DataPlaneDashboardPage';

const SiteTimelinesPage = React.lazy(() => import('./pages/SiteTimelinesPage'));
const AntennaViewPage = React.lazy(() => import('./pages/AntennaViewPage'));
const SitePage = React.lazy(() => import('./pages/SitePage'));
const ContactSearchPage = React.lazy(() => import('./pages/ContactSearchPage'));
const DeprecatedContactDetailPage = React.lazy(() => import('./pages/deprecated/ContactDetailsPage'));
const ContactDetailPage = React.lazy(() => import('./pages/ContactDetailsPage'));
const DeprecatedContactListPage = React.lazy(() => import('./pages/deprecated/ContactsListPage'));
const ContactListPage = React.lazy(() => import('./pages/ContactsListPage'));
const MaintenanceListPage = React.lazy(() => import('./pages/MaintenanceListPage'));
const MaintenanceSchedulePage = React.lazy(() => import('./pages/MaintenanceSchedulePage'));
const MaintenanceDetailsPage = React.lazy(() => import('./pages/MaintenanceDetailsPage'));
const DataExplorerPage = React.lazy(() => import('./pages/DataExplorerPage'));
const AuthorizationPage = React.lazy(() => import('./pages/AuthorizationPage'));
const OncallPage = React.lazy(() => import('./pages/OncallPage'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const SnapPage = React.lazy(() => import('./pages/SnapPage'));
const SnapSitePage = React.lazy(() => import('./pages/SnapSitePage'));
const CustomerOnboardingValidatorPage = React.lazy(() => import('./pages/CustomerOnboardingValidatorPage'));
const ListSatellitesPage = React.lazy(() => import('./pages/ListSatellitesPage'));
const SatelliteOnboardingPagePortedFromLegacy = React.lazy(
  () => import('./pages/SatelliteOnboardingPagePortedFromLegacy'),
);
const CppaOnboardingPage = React.lazy(() => import('./pages/CppaOnboardingPage'));
const UplinkRestrictionsPage = React.lazy(() => import('./pages/UplinkRestrictionsPage'));
const CustomerManagementViewCustomerPage = React.lazy(() => import('./pages/CustomerManagementViewCustomerPage'));
const CustomerManagementViewMultipleCustomersPage = React.lazy(
  () => import('./pages/CustomerManagementViewMultipleCustomersPage'),
);
const CustomerDetailPage = React.lazy(() => import('./pages/CustomerDetailPage'));
const MissionProfileDetailPage = React.lazy(() => import('./pages/MissionProfileDetailPage'));
const ConfigDetailPage = React.lazy(() => import('./pages/ConfigDetailPage'));
const SatelliteDetailPage = React.lazy(() => import('./pages/SatelliteDetailPage'));
const InventoryDashboardPage = React.lazy(() => import('./pages/InventoryDashboardPage'));
const InventoryPartsPage = React.lazy(() => import('./pages/InventoryPartsPage'));
const InventoryPartDetailPage = React.lazy(() => import('./pages/InventoryPartDetailPage'));
const InventorySitesPage = React.lazy(() => import('./pages/InventorySitesPage'));
const InventorySiteDetailPage = React.lazy(() => import('./pages/InventorySiteDetailPage'));
const InventoryInstanceDetailPage = React.lazy(() => import('./pages/InventoryInstanceDetailPage'));
const InventorySiteDirectoryPage = React.lazy(() => import('./pages/InventorySiteDirectoryPage'));
const InventoryRmaPage = React.lazy(() => import('./pages/InventoryRmaPage'));
const InventoryRmaAnalyticsPage = React.lazy(() => import('./pages/InventoryRmaAnalyticsPage'));
const InventoryOrdersPage = React.lazy(() => import('./pages/InventoryOrdersPage'));
const InventorySparesPage = React.lazy(() => import('./pages/InventorySparesPage'));

// Note well: order of pages matters here.
export default function App() {
  const [graphqlApiFeatureFlag] = useFeatureFlag(FeatureFlags.graphqlApi);
  return (
    <Switch>
      <Route exact path={Pages.SiteTimelines}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.SiteTimelines]} />}
        >
          <SiteTimelinesPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.AntennaView}>
        <React.Suspense fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home]} />}>
          <AntennaViewPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.Site}>
        <React.Suspense fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home]} />}>
          <SitePage />
        </React.Suspense>
      </Route>
      <Route exact path={Pages.SNAP}>
        <React.Suspense fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.SNAP]} />}>
          <SnapPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.SNAPSite}>
        <React.Suspense fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.SNAP]} />}>
          <SnapSitePage />
        </React.Suspense>
      </Route>
      <Route path={Pages.MaintenanceSchedule}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.MaintenanceSchedule]} />}
        >
          <MaintenanceSchedulePage />
        </React.Suspense>
      </Route>
      <Route path={Pages.MaintenanceDetails}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.MaintenanceList]} />}
        >
          <MaintenanceDetailsPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.MaintenanceList}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.MaintenanceList]} />}
        >
          <MaintenanceListPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.DataPlaneDashboard}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.DataPlaneDashboard]} />}
        >
          <DataPlaneDashboardPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.DataExplorer}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.DataExplorer]} />}
        >
          <DataExplorerPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.CustomerOnboardingValidator}>
        <React.Suspense
          fallback={
            <CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.CustomerOnboardingValidator]} />
          }
        >
          <CustomerOnboardingValidatorPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.ListSatellites}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ListSatellites]} />}
        >
          <ListSatellitesPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.SatelliteOnboarding}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.SatelliteOnboarding]} />}
        >
          <SatelliteOnboardingPagePortedFromLegacy />
        </React.Suspense>
      </Route>
      <Route path={Pages.CppaOnboarding}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.CppaOnboarding]} />}
        >
          <CppaOnboardingPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.UplinkRestrictions}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.UplinkRestrictions]} />}
        >
          <UplinkRestrictionsPage />
        </React.Suspense>
      </Route>
      <Route exact path={Pages.Contact}>
        <React.Suspense fallback={<CommonAppLayout breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ContactSearch]} />}>
          <ContactSearchPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.ContactDetails}>
        <React.Suspense fallback={<CommonAppLayout breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ContactSearch]} />}>
          {graphqlApiFeatureFlag ? <ContactDetailPage /> : <DeprecatedContactDetailPage />}
        </React.Suspense>
      </Route>
      <Route path={Pages.ContactList}>
        <React.Suspense fallback={<CommonAppLayout breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ListContacts]} />}>
          {graphqlApiFeatureFlag ? <ContactListPage /> : <DeprecatedContactListPage />}
        </React.Suspense>
      </Route>
      <Route path={Pages.Authorization}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.Authorization]} />}
        >
          <AuthorizationPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.Oncall}>
        <React.Suspense fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.Oncall]} />}>
          <OncallPage />
        </React.Suspense>
      </Route>
      <Route exact path={Pages.HomePage}>
        <React.Suspense fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home]} />}>
          <HomePage />
        </React.Suspense>
      </Route>
      <Route path={Pages.ViewMultipleCustomers}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ViewMultipleCustomers]} />}
        >
          <CustomerManagementViewMultipleCustomersPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.ViewCustomer}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ViewCustomer]} />}
        >
          <CustomerManagementViewCustomerPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.MissionProfileDetails}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ViewCustomer]} />}
        >
          <MissionProfileDetailPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.ConfigDetails}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ViewCustomer]} />}
        >
          <ConfigDetailPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.SatelliteDetails}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ViewCustomer]} />}
        >
          <SatelliteDetailPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.CustomerDetails}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.ViewCustomer]} />}
        >
          <CustomerDetailPage />
        </React.Suspense>
      </Route>
      <Route path={Pages.Inventory}>
        <React.Suspense
          fallback={<CommonAppLayout toolsHide breadcrumbs={[BREADCRUMBS.Home, BREADCRUMBS.Inventory]} />}
        >
          {/*
            One Suspense boundary for the whole section, then an inner switch. Detail routes
            precede their list routes because `/inventory/parts` prefix-matches
            `/inventory/parts/:partId`, and the dashboard is last because `/inventory` prefix-
            matches every other inventory route.
          */}
          <Switch>
            <Route path={Pages.InventoryPartDetail}>
              <InventoryPartDetailPage />
            </Route>
            <Route path={Pages.InventoryParts}>
              <InventoryPartsPage />
            </Route>
            <Route path={Pages.InventoryInstanceDetail}>
              <InventoryInstanceDetailPage />
            </Route>
            <Route path={Pages.InventorySiteDetail}>
              <InventorySiteDetailPage />
            </Route>
            <Route path={Pages.InventorySites}>
              <InventorySitesPage />
            </Route>
            <Route path={Pages.InventoryDirectory}>
              <InventorySiteDirectoryPage />
            </Route>
            {/* Before InventoryRma, which prefix-matches this. */}
            <Route path={Pages.InventoryRmaAnalytics}>
              <InventoryRmaAnalyticsPage />
            </Route>
            <Route path={Pages.InventoryRma}>
              <InventoryRmaPage />
            </Route>
            <Route path={Pages.InventoryOrders}>
              <InventoryOrdersPage />
            </Route>
            <Route path={Pages.InventorySpares}>
              <InventorySparesPage />
            </Route>
            <Route path={Pages.Inventory}>
              <InventoryDashboardPage />
            </Route>
          </Switch>
        </React.Suspense>
      </Route>
      <Redirect to={Pages.HomePage} />
    </Switch>
  );
}
